import { getDisplayEmail } from "@/lib/utils/display-email";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MessageCircleMore, CheckCircle2, Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { useTermSignatureRequest } from "@/hooks/useTermSignatureRequest";
import { shareTermViaWhatsApp } from "@/lib/terms/sharing";
import { maskPhoneInput } from "@/lib/utils/phone-mask";
import { formatCpf, stripCpf } from "@/lib/utils/cpf-mask";
import type { PatientTerm } from "@/hooks/usePatientTerms";
import { useUpdateOwner } from "@/hooks/useOwners";
import type { OwnerInput } from "@/lib/validations/owner";

export interface OwnerProfileForSignature {
  id: string;
  name: string;
  gender_id: string | null;
  birth_date: string | null;
  profession: string | null;
  marketing_consent: boolean;
  lead_source_id: string | null;
  phones: { phone_number: string; is_whatsapp: boolean; is_primary: boolean }[];
  addresses: {
    zip_code: string;
    street: string;
    number: string;
    complement: string;
    neighborhood: string;
    city_id: string;
    is_primary: boolean;
  }[];
}

export interface SendForSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  term: PatientTerm;
  petId: string;
  vetName: string;
  /** Whether the current vet has an A1 certificate loaded (recorded on the vet signatory). */
  hasCertificate: boolean;
  /** Whether this term requires a vet signatory — gates the leading confirmation step. */
  requiresVetSignature: boolean;
  vetEmail: string;
  vetCrmv?: string;
  vetMapa?: string;
  prefill: { name?: string; cpf?: string; email?: string; phone?: string };
  /** Owner id to persist the typed CPF/e-mail onto (null if the owner couldn't be resolved). */
  ownerId: string | null;
  /** Snapshot of the owner's other profile fields, needed because owner updates fully replace phones/addresses. */
  ownerProfile: OwnerProfileForSignature | null;
  documentTypes: { id: string; name: string; mask: string | null }[] | undefined;
}

export function SendForSignatureDialog({
  open,
  onOpenChange,
  term,
  petId,
  vetName,
  hasCertificate,
  requiresVetSignature,
  vetEmail,
  vetCrmv,
  vetMapa,
  prefill,
  ownerId,
  ownerProfile,
  documentTypes,
}: SendForSignatureDialogProps) {
  const { t } = useTranslation("terms");
  const createRequest = useTermSignatureRequest();
  const updateOwner = useUpdateOwner();

  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vetConfirmed, setVetConfirmed] = useState(false);
  const [vetTypedName, setVetTypedName] = useState("");

  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  const ownerProfileRef = useRef(ownerProfile);
  ownerProfileRef.current = ownerProfile;
  const vetNameRef = useRef(vetName);
  vetNameRef.current = vetName;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // Only reset the dialog when it transitions from closed to open — not on every
    // render while it stays open, since `prefill` is a new object each render and
    // switching browser tabs (visibility change) would otherwise wipe out the
    // sign-url/whatsapp step that was already reached.
    if (open && !wasOpenRef.current) {
      const currentPrefill = prefillRef.current;
      // Prefer the responsável's number already flagged as WhatsApp; fall back to
      // whichever phone is prefilled (e.g. their primary phone) otherwise.
      const savedWhatsappPhone = ownerProfileRef.current?.phones.find((p) => p.is_whatsapp)?.phone_number;
      setName(currentPrefill.name ?? "");
      setCpf(formatCpf(currentPrefill.cpf ?? ""));
      setEmail(currentPrefill.email ?? "");
      setPhone(maskPhoneInput(savedWhatsappPhone || currentPrefill.phone || ""));
      setSignUrl(null);
      setCompleted(false);
      setShowWhatsapp(false);
      setError(null);
      setVetConfirmed(false);
      setVetTypedName(vetNameRef.current);
    }
    wasOpenRef.current = open;
  }, [open]);

  const validationError = useMemo(() => {
    if (!name.trim()) return "send.errName";
    if (stripCpf(cpf).length !== 11) return "send.errCpf";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return "send.errEmail";
    return null;
  }, [name, cpf, email]);

  const handleSubmit = async () => {
    setError(null);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (ownerId && ownerProfile) {
      const cpfTypeId = documentTypes?.find((docType) => docType.name.toUpperCase() === "CPF")?.id;
      if (!cpfTypeId) {
        console.warn("No 'CPF' document type found; skipping owner profile CPF/email save.");
      }
      if (cpfTypeId) {
        const ownerInput: OwnerInput = {
          name: ownerProfile.name,
          gender_id: ownerProfile.gender_id ?? "",
          document_type_id: cpfTypeId,
          document: stripCpf(cpf),
          birth_date: ownerProfile.birth_date ?? "",
          profession: ownerProfile.profession ?? "",
          notes: "",
          marketing_consent: ownerProfile.marketing_consent,
          lead_source_id: ownerProfile.lead_source_id ?? "",
          phones: ownerProfile.phones,
          emails: [{ email: email.trim(), is_primary: true }],
          addresses: ownerProfile.addresses,
        };
        try {
          await updateOwner.mutateAsync({ id: ownerId, input: ownerInput });
        } catch (e) {
          console.error("Failed to save owner profile before generating signature request", e);
          setError("send.errorGeneric");
          return;
        }
      }
    }

    try {
      const res = await createRequest.mutateAsync({
        patient_term_id: term.id,
        vet_name: vetTypedName.trim() || vetName || "Veterinário",
        vet_has_certificate: hasCertificate,
        vet_email: vetEmail,
        vet_crmv: vetCrmv,
        vet_mapa: vetMapa,
        responsible: { name: name.trim(), cpf: stripCpf(cpf), email: email.trim() },
        pet_id: petId,
      });
      if (res.completed) {
        setCompleted(true);
      } else {
        setSignUrl(res.sign_url);
      }
    } catch (e) {
      const code = (e as Error).message;
      setError(`send.errorGeneric`);
      toast.error(t(`signature.errors.${code}`, { defaultValue: t("signature.send.errorGeneric") }));
    }
  };

  const persistWhatsappPhone = async () => {
    if (!ownerId || !ownerProfile) return;
    const digitsOnly = (value: string) => value.replace(/\D/g, "");
    const typedDigits = digitsOnly(phone);
    const existingMatch = ownerProfile.phones.find((p) => digitsOnly(p.phone_number) === typedDigits);
    // Number is already on file and already flagged as WhatsApp — nothing to persist.
    if (existingMatch?.is_whatsapp) return;

    const cpfTypeId = documentTypes?.find((docType) => docType.name.toUpperCase() === "CPF")?.id;
    if (!cpfTypeId) return;

    const updatedPhones = existingMatch
      ? ownerProfile.phones.map((p) =>
          digitsOnly(p.phone_number) === typedDigits ? { ...p, is_whatsapp: true } : p,
        )
      : [
          ...ownerProfile.phones,
          { phone_number: phone.trim(), is_whatsapp: true, is_primary: ownerProfile.phones.length === 0 },
        ];

    const ownerInput: OwnerInput = {
      name: ownerProfile.name,
      gender_id: ownerProfile.gender_id ?? "",
      document_type_id: cpfTypeId,
      document: stripCpf(cpf),
      birth_date: ownerProfile.birth_date ?? "",
      profession: ownerProfile.profession ?? "",
      notes: "",
      marketing_consent: ownerProfile.marketing_consent,
      lead_source_id: ownerProfile.lead_source_id ?? "",
      phones: updatedPhones,
      emails: [{ email: email.trim(), is_primary: true }],
      addresses: ownerProfile.addresses,
    };

    try {
      await updateOwner.mutateAsync({ id: ownerId, input: ownerInput });
    } catch (e) {
      console.error("Failed to save responsável's WhatsApp number", e);
    }
  };

  const handleWhatsApp = async () => {
    if (!signUrl || !phone.trim()) return;
    const message = `${t("signature.send.title")}: ${term.rendered_title || term.title}\n${signUrl}`;
    try {
      shareTermViaWhatsApp(phone, message);
    } catch {
      toast.error(t("share.sendError"));
      return;
    }
    await persistWhatsappPhone();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {!vetConfirmed && requiresVetSignature ? t("signature.send.vetConfirm.title") : t("signature.send.title")}
          </DialogTitle>
          <DialogDescription>
            {!vetConfirmed && requiresVetSignature
              ? t("signature.send.vetConfirm.description")
              : t("signature.send.description")}
          </DialogDescription>
        </DialogHeader>

        {!vetConfirmed && requiresVetSignature ? (
          <div className="space-y-4 py-2">
            <div className="space-y-2 rounded-lg border p-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("signature.send.vetConfirm.nameLabel")}</span>
                <span className="font-medium">{vetName || "—"}</span>
              </div>
              {vetCrmv && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("signature.send.vetConfirm.crmvLabel")}</span>
                  <span className="font-medium">{vetCrmv}</span>
                </div>
              )}
              {vetMapa && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("signature.send.vetConfirm.mapaLabel")}</span>
                  <span className="font-medium">{vetMapa}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("signature.send.vetConfirm.emailLabel")}</span>
                <span className="font-medium">{getDisplayEmail(vetEmail) || "—"}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vet-sign-name">{t("signature.send.vetConfirm.typedNameLabel")}</Label>
              <Input
                id="vet-sign-name"
                placeholder={t("signature.send.vetConfirm.typedNamePlaceholder")}
                value={vetTypedName}
                onChange={(e) => setVetTypedName(e.target.value)}
              />
            </div>
            {vetTypedName.trim() && (
              <div className="rounded-lg border bg-background p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  {t("signature.sign.previewLabel")}
                </p>
                <p className="font-signature text-4xl leading-none">{vetTypedName}</p>
              </div>
            )}
          </div>
        ) : completed ? (
          <div className="flex items-center gap-2 py-4 text-sm">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            {t("signature.send.completedNoLink")}
          </div>
        ) : signUrl ? (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Mail className="h-5 w-5 text-emerald-600" />
              {t("signature.send.emailSentTitle")}
            </div>
            {!showWhatsapp ? (
              <Button type="button" variant="outline" onClick={() => setShowWhatsapp(true)}>
                <MessageCircleMore className="mr-2 h-4 w-4" />
                {t("signature.send.wantWhatsapp")}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sig-phone">{t("signature.send.phoneLabel")}</Label>
                  <Input
                    id="sig-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(maskPhoneInput(e.target.value))}
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => void handleWhatsApp()}
                  disabled={!phone.trim() || updateOwner.isPending}
                >
                  <MessageCircleMore className="mr-2 h-4 w-4" />
                  {t("signature.send.sendWhatsapp")}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{t("signature.send.missingDataTitle")}</p>
            <div className="space-y-2">
              <Label htmlFor="sig-name">{t("signature.send.nameLabel")}</Label>
              <Input id="sig-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sig-cpf">{t("signature.send.cpfLabel")}</Label>
                <Input
                  id="sig-cpf"
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  maxLength={14}
                  value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sig-email">{t("signature.send.emailLabel")}</Label>
                <Input id="sig-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>
            {error && (
              <p className="text-sm text-destructive">
                {t(`signature.${error}`, { defaultValue: t("signature.send.errorGeneric") })}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {!vetConfirmed && requiresVetSignature ? (
            <Button type="button" onClick={() => setVetConfirmed(true)} disabled={!vetTypedName.trim()}>
              {t("signature.send.vetConfirm.submit")}
            </Button>
          ) : (
            !completed &&
            !signUrl && (
              <Button type="button" onClick={handleSubmit} disabled={createRequest.isPending || updateOwner.isPending}>
                {createRequest.isPending || updateOwner.isPending ? t("signature.send.sending") : t("signature.send.submit")}
              </Button>
            )
          )}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("share.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
